import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AuthLayout, { CornerLogo, AUTH } from './AuthLayout';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const passwordMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const canSubmit = newPassword.length >= 8 && confirmPassword.length > 0 && !passwordMismatch;

  const handleSubmit = async () => {
    setError('');
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) return;
    if (!token) {
      setError('Invalid or missing reset link. Please request a new one.');
      return;
    }
    setLoading(true);
    try {
      await axios.post('/api/auth/reset-password', { token, newPassword: newPassword.trim() });
      navigate('/auth/login', { state: { passwordReset: true } });
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const code = err.response?.data?.code as string | undefined;
        if (code === 'TOKEN_EXPIRED' || code === 'TOKEN_INVALID') {
          setError('This reset link has expired. Please request a new one.');
        } else {
          setError('Something went wrong. Please try again.');
        }
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const eyeIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9B8E83" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

  return (
    <AuthLayout>
      <div
        style={{
          width: '100%',
          maxWidth: 480,
          backgroundColor: AUTH.cardBg,
          borderRadius: 16,
          padding: 40,
          boxShadow: 'var(--auth-card-shadow-box)',
          fontFamily: '"Geist", sans-serif',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginBottom: 28,
            gap: 16,
          }}
        >
          <CornerLogo />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 22,
                fontWeight: 700,
                color: AUTH.textPrimary,
                letterSpacing: '-0.03em',
                lineHeight: '28px',
                textAlign: 'center',
              }}
            >
              Choose a new password
            </h1>
            <p
              style={{
                margin: 0,
                fontSize: 14,
                fontWeight: 400,
                color: AUTH.textSecondary,
                lineHeight: '18px',
                textAlign: 'center',
              }}
            >
              Must be at least 8 characters.
            </p>
          </div>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        {/* New password */}
        <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 14, gap: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 500, color: AUTH.textPrimary, lineHeight: '16px' }}>
            New password
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoFocus
              style={{
                width: '100%',
                borderRadius: 8,
                border: `1.5px solid ${AUTH.inputBorder}`,
                backgroundColor: '#FAFAF9',
                padding: '12px 40px 12px 14px',
                fontSize: 14,
                fontFamily: '"Geist", sans-serif',
                color: AUTH.textPrimary,
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => { e.target.style.borderColor = AUTH.accent; }}
              onBlur={(e) => { e.target.style.borderColor = AUTH.inputBorder; }}
            />
            <button
              type="button"
              onClick={() => setShowNew(!showNew)}
              style={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                display: 'flex',
              }}
            >
              {eyeIcon}
            </button>
          </div>
        </div>

        {/* Confirm password */}
        <div style={{ display: 'flex', flexDirection: 'column', marginBottom: passwordMismatch ? 6 : 14, gap: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 500, color: AUTH.textPrimary, lineHeight: '16px' }}>
            Confirm password
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={{
                width: '100%',
                borderRadius: 8,
                border: `1.5px solid ${passwordMismatch ? AUTH.textError : AUTH.inputBorder}`,
                backgroundColor: passwordMismatch ? '#FDF5F3' : '#FAFAF9',
                padding: '12px 40px 12px 14px',
                fontSize: 14,
                fontFamily: '"Geist", sans-serif',
                color: AUTH.textPrimary,
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => { e.target.style.borderColor = passwordMismatch ? AUTH.textError : AUTH.accent; }}
              onBlur={(e) => { e.target.style.borderColor = passwordMismatch ? AUTH.textError : AUTH.inputBorder; }}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              style={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                display: 'flex',
              }}
            >
              {eyeIcon}
            </button>
          </div>
          {passwordMismatch && (
            <span style={{ fontSize: 12, color: AUTH.textError, fontWeight: 400, lineHeight: '16px' }}>
              Passwords don&apos;t match
            </span>
          )}
        </div>

        {error && (
          <p style={{ margin: '0 0 12px', fontSize: 12, color: AUTH.textError, textAlign: 'center' }}>
            {error}
          </p>
        )}

        {/* CTA */}
        <button
          type="submit"
          disabled={!canSubmit || loading}
          style={{
            width: '100%',
            borderRadius: 8,
            padding: '13px',
            backgroundColor: canSubmit ? AUTH.accent : '#E8C4B0',
            border: 'none',
            color: '#FFFFFF',
            fontSize: 14,
            fontWeight: 600,
            fontFamily: '"Geist", sans-serif',
            cursor: canSubmit && !loading ? 'pointer' : 'not-allowed',
            marginTop: 8,
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {loading ? 'Updating\u2026' : 'Update password \u2192'}
        </button>
        </form>

        {/* Back link */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={() => navigate('/auth/login')}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 400,
              color: AUTH.textSecondary,
              fontFamily: '"Geist", sans-serif',
              lineHeight: '16px',
            }}
          >
            &larr; Back to sign in
          </button>
        </div>
      </div>
    </AuthLayout>
  );
}
